import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../../lib/api";
import { STATUS_COLORS } from "../../lib/constants";
import { fireConfetti } from "../../components/Confetti";
import { formatApiErrorDetail, dateFormat } from "../../lib/api";
import { AlertTriangle } from "lucide-react";

export default function EmpfehlungSeite() {
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") === "list" ? "list" : "form";
  const [tab, setTab] = useState(initialTab);
  const [empfehlungen, setEmpfehlungen] = useState([]);
  const [refresh, setRefresh] = useState(0);

  useEffect(() => {
    api.get("/empfehlungen").then((r) => setEmpfehlungen(r.data));
  }, [refresh]);

  return (
    <div className="space-y-6" data-testid="empfehlungen-page">
      <div>
        <h1 className="text-3xl font-heading font-bold text-falke-blue">Empfehlungen</h1>
        <p className="text-falke-anthracite/70 mt-1">Empfiehl FalkeTek weiter und verdiene FalkeCoins.</p>
      </div>

      <div className="inline-flex bg-white rounded-xl p-1 border border-falke-blue/10 shadow-sm">
        <button
          onClick={() => setTab("form")}
          data-testid="tab-form"
          className={`px-4 py-2 rounded-lg text-sm font-semibold ${tab === "form" ? "bg-falke-orange text-white" : "text-falke-blue"}`}
        >
          Neue Empfehlung
        </button>
        <button
          onClick={() => setTab("list")}
          data-testid="tab-list"
          className={`px-4 py-2 rounded-lg text-sm font-semibold ${tab === "list" ? "bg-falke-orange text-white" : "text-falke-blue"}`}
        >
          Meine Empfehlungen ({empfehlungen.length})
        </button>
      </div>

      {tab === "form" ? <NeueEmpfehlungForm onSuccess={() => { fireConfetti("normal"); setRefresh((x) => x + 1); setTab("list"); }} /> : <EmpfehlungListe items={empfehlungen} />}
    </div>
  );
}

function NeueEmpfehlungForm({ onSuccess }) {
  const [form, setForm] = useState({
    empfohlener_name: "",
    empfohlener_telefon: "",
    empfohlener_email: "",
    empfohlener_strasse: "",
    notiz: "",
    einwilligung_empfohlener: false,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [duplikatWarn, setDuplikatWarn] = useState(null); // { treffer: [...] }

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const submit = async (e, duplikatBestaetigt = false) => {
    if (e?.preventDefault) e.preventDefault();
    setError(""); setSuccess(""); setLoading(true);
    try {
      // Leere optionale Felder als null senden (sonst lehnt Pydantic EmailStr ab)
      const payload = {
        empfohlener_name: form.empfohlener_name?.trim(),
        empfohlener_telefon: form.empfohlener_telefon?.trim(),
        empfohlener_email: form.empfohlener_email?.trim() || null,
        empfohlener_strasse: form.empfohlener_strasse?.trim() || null,
        notiz: form.notiz?.trim() || null,
        einwilligung_empfohlener: form.einwilligung_empfohlener,
        duplikat_bestaetigt: duplikatBestaetigt,
      };
      await api.post("/empfehlungen", payload);
      setSuccess("Empfehlung erfolgreich angelegt!");
      setForm({ empfohlener_name: "", empfohlener_telefon: "", empfohlener_email: "", empfohlener_strasse: "", notiz: "", einwilligung_empfohlener: false });
      setDuplikatWarn(null);
      onSuccess();
    } catch (err) {
      // Dubletten-Warnung: Backend liefert 409 mit detail.code === "duplikat"
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409 && detail && typeof detail === "object" && detail.code === "duplikat") {
        setDuplikatWarn({ treffer: detail.treffer || [], message: detail.message || "Mögliche Dublette" });
      } else {
        setError(formatApiErrorDetail(detail) || "Empfehlung konnte nicht gespeichert werden.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <form onSubmit={(e) => submit(e, false)} className="falke-card p-6 sm:p-8 max-w-2xl space-y-4" data-testid="neue-empfehlung-form">
        <div>
          <label className="block text-sm font-medium text-falke-blue mb-1.5">Name der empfohlenen Person *</label>
          <input required value={form.empfohlener_name} onChange={upd("empfohlener_name")} className="w-full px-4 py-3 rounded-xl border border-falke-blue/15 focus:outline-none focus:ring-2 focus:ring-falke-orange/40" data-testid="emp-name" />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-falke-blue mb-1.5">Telefon *</label>
            <input required value={form.empfohlener_telefon} onChange={upd("empfohlener_telefon")} className="w-full px-4 py-3 rounded-xl border border-falke-blue/15" data-testid="emp-tel" />
          </div>
          <div>
            <label className="block text-sm font-medium text-falke-blue mb-1.5">E-Mail</label>
            <input type="email" value={form.empfohlener_email} onChange={upd("empfohlener_email")} className="w-full px-4 py-3 rounded-xl border border-falke-blue/15" data-testid="emp-email" />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-falke-blue mb-1.5">Straße + Hausnummer</label>
          <input value={form.empfohlener_strasse} onChange={upd("empfohlener_strasse")} placeholder="z.B. Sonnenweg 12" className="w-full px-4 py-3 rounded-xl border border-falke-blue/15" data-testid="emp-strasse" />
          <p className="text-xs text-falke-anthracite/60 mt-1">Hilft uns, Dubletten zu erkennen — z. B. wenn jemand schon empfohlen wurde.</p>
        </div>
        <div>
          <label className="block text-sm font-medium text-falke-blue mb-1.5">Persönliche Notiz</label>
          <textarea rows={3} value={form.notiz} onChange={upd("notiz")} className="w-full px-4 py-3 rounded-xl border border-falke-blue/15" placeholder="z.B. Hat Interesse an PV+Speicher" data-testid="emp-notiz" />
        </div>
        <label className="flex items-start gap-3 cursor-pointer text-sm">
          <input required type="checkbox" checked={form.einwilligung_empfohlener} onChange={upd("einwilligung_empfohlener")} className="mt-1 w-4 h-4 accent-falke-orange" data-testid="emp-einwilligung" />
          <span>Ich habe die Erlaubnis der empfohlenen Person, FalkeTek den Kontakt zu geben (DSGVO).</span>
        </label>
        {error && <div className="text-sm text-red-600" data-testid="emp-error">{error}</div>}
        {success && <div className="text-sm text-falke-success" data-testid="emp-success">{success}</div>}
        <button type="submit" disabled={loading} className="btn-primary w-full sm:w-auto" data-testid="emp-submit">{loading ? "Sende…" : "Empfehlung senden"}</button>
      </form>

      {duplikatWarn && (
        <DuplikatModal
          warn={duplikatWarn}
          form={form}
          onCancel={() => setDuplikatWarn(null)}
          onConfirm={() => submit(null, true)}
          loading={loading}
        />
      )}
    </>
  );
}

function DuplikatModal({ warn, form, onCancel, onConfirm, loading }) {
  return (
    <div className="fixed inset-0 bg-falke-blue/30 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="duplikat-modal">
      <div className="bg-white rounded-2xl max-w-lg w-full p-6 sm:p-7 shadow-xl">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-11 h-11 rounded-2xl bg-falke-orange/15 flex items-center justify-center flex-shrink-0">
            <AlertTriangle className="text-falke-orange" size={22} />
          </div>
          <div>
            <h3 className="font-heading text-lg text-falke-blue">Mögliche Dublette gefunden</h3>
            <p className="text-sm text-falke-anthracite/70 mt-0.5">
              Es gibt bereits eine Empfehlung für <span className="font-semibold text-falke-blue">{form.empfohlener_name}</span>{" "}
              in <span className="font-semibold text-falke-blue">{form.empfohlener_strasse}</span> — trotzdem einreichen?
            </p>
          </div>
        </div>
        <div className="space-y-2 mb-5 max-h-48 overflow-y-auto">
          {warn.treffer.map((t) => (
            <div key={t.id} className="rounded-xl border border-falke-blue/15 bg-falke-warmwhite p-3 text-sm" data-testid={`duplikat-${t.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="font-semibold text-falke-blue">{t.empfohlener_name}</span>
                <span className="status-badge text-xs bg-falke-blue/10 text-falke-blue">{t.status}</span>
              </div>
              <div className="text-xs text-falke-anthracite/70 mt-1">{t.empfohlener_strasse} · eingereicht {t.erstellt_am ? new Date(t.erstellt_am).toLocaleDateString("de-DE") : "—"}</div>
            </div>
          ))}
        </div>
        <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-end">
          <button type="button" onClick={onCancel} className="btn-outline" disabled={loading} data-testid="duplikat-cancel">Abbrechen</button>
          <button type="button" onClick={onConfirm} className="btn-primary" disabled={loading} data-testid="duplikat-confirm">
            {loading ? "Sende…" : "Trotzdem einreichen"}
          </button>
        </div>
      </div>
    </div>
  );
}

function EmpfehlungListe({ items }) {
  if (items.length === 0)
    return <div className="falke-card p-10 text-center text-falke-anthracite/60" data-testid="empfehlungen-empty">Noch keine Empfehlungen.</div>;
  return (
    <div className="space-y-3" data-testid="empfehlungen-list">
      {items.map((e) => {
        const fertigCoins = e.status === "Fertig" && e.coins_ausgezahlt_am;
        return (
          <div key={e.id} className={`falke-card p-5 ${fertigCoins ? "ring-2 ring-falke-gold" : ""}`} data-testid={`liste-${e.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="font-heading text-lg text-falke-blue">{e.empfohlener_name}</div>
                {e.empfohlener_strasse && <div className="text-xs text-falke-anthracite/70">{e.empfohlener_strasse}</div>}
                <div className="text-xs text-falke-anthracite/60">eingereicht am {dateFormat(e.erstellt_am)}</div>
                {e.notiz && <div className="text-sm text-falke-anthracite/80 mt-2">{e.notiz}</div>}
              </div>
              <span className={`status-badge ${STATUS_COLORS[e.status] || "bg-gray-100"}`}>{e.status}</span>
            </div>
            {e.auftragswert_netto && (
              <div className="text-xs text-falke-blue mt-2">Auftragswert (netto): {Number(e.auftragswert_netto).toFixed(2)} €</div>
            )}
            {fertigCoins && (
              <div className="mt-3 p-3 rounded-xl bg-gradient-to-r from-falke-gold/30 to-falke-orange/20 text-falke-blue text-sm font-semibold" data-testid={`celebrate-${e.id}`}>
                🎉 FalkeCoins gutgeschrieben am {dateFormat(e.coins_ausgezahlt_am)}!
              </div>
            )}
            {e.status === "Fertig" && !e.coins_ausgezahlt_am && (
              <div className="mt-3 p-3 rounded-xl bg-falke-blue/[0.04] text-falke-blue text-sm">
                ✓ Anlage fertig. Deine FalkeCoins kommen, sobald die Vollzahlung eingegangen ist (max. 7 Tage).
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
